import { useState, useEffect, useMemo } from 'react';
import {
  subscribeToUsers,
  updateUserFields,
  setUserDisabled,
  cancelPendingDeletion,
  closeAccountOnBehalf,
  purgeAccountNow,
  deleteUserTombstone,
  getOrphanUsers,
  deleteOrphanUser,
  getUserAuthMetadata,
  getUserPushDeviceState,
  getUserAiCreditStatus,
  grantAiCredits,
  setUserAiCapOverride,
  setUserAiRateLimitMode,
  type OrphanUser,
  type AuthMetadata,
  type PushDeviceState,
  type AiCreditStatus,
} from '../../services/firebase';
import { fetchAiRateLimitConfig } from '../../services/aiRateLimit';
import {
  checkCapValue,
  type AiRateLimitConfig,
  type AiRateLimitMode,
  type CapValue,
} from '../../services/aiRateLimitConfig';
import { AiCapOverrideField } from './AiCapOverrideField';
import { AiRateLimitModeField } from './AiRateLimitModeField';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../common/Button';
import { Badge } from '../common/Badge';
import { Modal } from '../common/Modal';
import { CreateBrandUserModal } from './CreateBrandUserModal';
import { UserInfoScreen } from './UserInfoScreen';
import { paginate, UsersPager } from './UsersPager';
import {
  deriveSubscriptionBadge,
  SUBSCRIPTION_FILTERS,
  type SubscriptionKind,
} from './subscriptionState';
import {
  derivePushBadge,
  matchesPushFilter,
  PUSH_FILTERS,
  type PushFilterKey,
} from './pushDeviceState';
import { formatCompactAge, toDateOrNull } from './userDiagnostics';
import type {
  AccountStatus,
  BrandMembershipRole,
  FirestoreUser,
  UserPlan,
} from '../../types';
import toast from 'react-hot-toast';

function getAccountStatus(user: FirestoreUser): AccountStatus {
  if (user.accountStatus) return user.accountStatus;
  // Backward compat: fall back to the legacy `disabled` boolean
  return user.disabled ? 'disabled' : 'active';
}

/**
 * User-type filter keys. These mirror the role flags on the Firestore user doc.
 * Note: there is no separate `roles`/`articleCreator` field in this data model —
 * the article-creating role is `isContributor` (see the Edit modal copy), so it
 * is covered by the "Contributors" filter rather than a distinct chip.
 */
type UserTypeKey = 'admin' | 'contributor' | 'brandAdmin' | 'brandMember' | 'regular';

const USER_TYPE_FILTERS: { key: UserTypeKey; label: string }[] = [
  { key: 'admin', label: 'Admins' },
  { key: 'contributor', label: 'Contributors' },
  { key: 'brandAdmin', label: 'Brand Admins' },
  { key: 'brandMember', label: 'Brand Members' },
  { key: 'regular', label: 'Regular' },
];

function hasBrandRole(u: FirestoreUser, role: BrandMembershipRole): boolean {
  return (u.brandMemberships ?? []).some((m) => m.role === role);
}

/** A "regular" user carries none of the special role flags or brand memberships. */
function isRegularUser(u: FirestoreUser): boolean {
  return !u.isAdmin && !u.isContributor && (u.brandMemberships ?? []).length === 0;
}

function matchesUserType(u: FirestoreUser, type: UserTypeKey): boolean {
  switch (type) {
    case 'admin':
      return u.isAdmin === true;
    case 'contributor':
      return u.isContributor === true;
    case 'brandAdmin':
      return hasBrandRole(u, 'brandAdmin');
    case 'brandMember':
      return hasBrandRole(u, 'brandMember');
    case 'regular':
      return isRegularUser(u);
  }
}

interface StatusBadgeProps {
  status: AccountStatus;
}

function StatusBadge({ status }: StatusBadgeProps) {
  switch (status) {
    case 'disabled':
      return <Badge variant="danger">Disabled</Badge>;
    case 'pendingDeletion':
      return <Badge variant="warning">Pending Deletion</Badge>;
    case 'deleted':
      return <Badge variant="default">Deleted</Badge>;
    case 'active':
    default:
      return <Badge variant="success">Active</Badge>;
  }
}

function toDate(timestamp: unknown): Date | null {
  if (!timestamp) return null;
  if (
    typeof timestamp === 'object' &&
    'toDate' in timestamp &&
    typeof (timestamp as { toDate: unknown }).toDate === 'function'
  ) {
    return (timestamp as { toDate: () => Date }).toDate();
  }
  if (typeof timestamp === 'object' && 'seconds' in timestamp) {
    return new Date((timestamp as { seconds: number }).seconds * 1000);
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  return null;
}

function formatDate(timestamp: unknown): string {
  const date = toDate(timestamp);
  if (!date) return '-';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Format an ISO date string (from Firebase Auth metadata) as a relative time, e.g. "2 mo ago". */
function formatRelativeDate(iso: string | undefined): string {
  if (!iso) return '-';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  return formatDistanceToNow(date, { addSuffix: true });
}

// ---------------------------------------------------------------------------
// Sorting & filtering
// ---------------------------------------------------------------------------

// The sortable columns of the trimmed list. Sorts for columns that moved to the
// User Info screen (status, watches, app version, separate last login) went
// with them; a saved view naming one falls back in loadViewState.
type SortField = 'user' | 'plan' | 'created' | 'lastActive';
type SortDir = 'asc' | 'desc';

/** Rank for the plan/tier ordering (free is lowest). */
const PLAN_RANK: Record<UserPlan, number> = {
  free: 0,
  collector: 1,
  curator: 2,
  horologist: 3,
};

const PLAN_FILTERS: { key: UserPlan; label: string }[] = [
  { key: 'free', label: 'Free' },
  { key: 'collector', label: 'Collector' },
  { key: 'curator', label: 'Curator' },
  { key: 'horologist', label: 'Horologist' },
];

const STATUS_FILTERS: { key: AccountStatus; label: string }[] = [
  { key: 'active', label: 'Active' },
  { key: 'disabled', label: 'Disabled' },
  { key: 'pendingDeletion', label: 'Pending Deletion' },
  { key: 'deleted', label: 'Deleted' },
];

type DatePreset = 'any' | '7d' | '30d' | '90d' | 'year';

const DATE_PRESETS: { key: DatePreset; label: string }[] = [
  { key: 'any', label: 'Any time' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: '90d', label: 'Last 90 days' },
  { key: 'year', label: 'This year' },
];

const DAY_MS = 24 * 60 * 60 * 1000;

/** Lower bound (ms) for a created-date preset, or null for "any time". */
function datePresetCutoffMs(preset: DatePreset): number | null {
  const now = Date.now();
  switch (preset) {
    case '7d':
      return now - 7 * DAY_MS;
    case '30d':
      return now - 30 * DAY_MS;
    case '90d':
      return now - 90 * DAY_MS;
    case 'year':
      return new Date(new Date().getFullYear(), 0, 1).getTime();
    case 'any':
    default:
      return null;
  }
}

/** Resolved creation time (ms): auth metadata is authoritative, Firestore createdAt is the fallback. */
function resolvedCreatedMs(u: FirestoreUser, meta: AuthMetadata | undefined): number | null {
  if (meta?.creationTime) {
    const t = new Date(meta.creationTime).getTime();
    if (!Number.isNaN(t)) return t;
  }
  const d = toDate(u.createdAt);
  return d ? d.getTime() : null;
}

/** Resolved last-login time (ms): auth lastSignInTime is authoritative, Firestore lastLoginAt is the fallback. */
function resolvedLastLoginMs(u: FirestoreUser, meta: AuthMetadata | undefined): number | null {
  if (meta?.lastSignInTime) {
    const t = new Date(meta.lastSignInTime).getTime();
    if (!Number.isNaN(t)) return t;
  }
  const d = toDate(u.lastLoginAt);
  return d ? d.getTime() : null;
}

/**
 * Last active: the latest app open, else the Auth token refresh, else the last
 * sign-in. App open is the signal anyone wants; the other two fill in for users
 * on a build that does not report it yet.
 */
function lastActive(
  u: FirestoreUser,
  meta: AuthMetadata | undefined,
): { ms: number; source: 'app' | 'refresh' | 'signIn' } | null {
  const opened = toDateOrNull(u.lastAppOpenedAt)?.getTime();
  if (opened) return { ms: opened, source: 'app' };
  const refresh = meta?.lastRefreshTime ? new Date(meta.lastRefreshTime).getTime() : NaN;
  if (!Number.isNaN(refresh)) return { ms: refresh, source: 'refresh' };
  const signIn = resolvedLastLoginMs(u, meta);
  return signIn === null ? null : { ms: signIn, source: 'signIn' };
}

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
  return (
    <span className={`ml-1 inline-block ${active ? 'text-brand-600' : 'text-gray-300'}`}>
      {active && dir === 'desc' ? '▼' : '▲'}
    </span>
  );
}

/** A pill toggle used across the filter rows (type / plan / status). */
function FilterChip({
  active,
  label,
  count,
  onClick,
}: {
  active: boolean;
  label: string;
  count?: number;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 text-sm rounded-full px-3 py-1 border transition-colors ${
        active
          ? 'bg-brand-600 text-white border-brand-600'
          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
      }`}
    >
      {label}
      {count !== undefined && (
        <span className={active ? 'text-brand-200' : 'text-gray-400'}>({count})</span>
      )}
    </button>
  );
}

/** A clickable, sortable table header cell. */
function SortableHeader({
  label,
  field,
  sortField,
  sortDir,
  onSort,
  align = 'left',
  className = '',
  title,
}: {
  label: string;
  field: SortField;
  sortField: SortField;
  sortDir: SortDir;
  onSort: (field: SortField) => void;
  align?: 'left' | 'right';
  className?: string;
  title?: string;
}) {
  const active = sortField === field;
  const alignClass = align === 'right' ? 'text-right' : 'text-left';
  return (
    <th
      onClick={() => onSort(field)}
      title={title}
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={`px-4 py-3 ${alignClass} text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700 ${className}`}
    >
      {label}
      <SortIcon active={active} dir={sortDir} />
    </th>
  );
}

/** Debounce a rapidly-changing value (e.g. a search box) by `delayMs`. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

// --- Persisted view state (sort + filters) -------------------------------

const VIEW_STATE_KEY = 'userManagement.viewState';

/**
 * Bump when a default filter meaning changes so persisted blobs get migrated.
 * v2: default status filter hides `deleted` (tombstoned) users — pre-v2 blobs
 * (which stored `[]` = show all) are migrated to the new hide-deleted default.
 */
const VIEW_STATE_VERSION = 2;

interface PersistedViewState {
  version: number;
  sortField: SortField;
  sortDir: SortDir;
  selectedTypes: UserTypeKey[];
  planFilter: UserPlan[];
  statusFilter: AccountStatus[];
  datePreset: DatePreset;
  /** When true, show only users with `submissionDisabled` set. */
  submissionDisabledOnly: boolean;
  /**
   * WIDENING, unlike every other filter here: false (the default) HIDES test
   * accounts, true adds them back. They are noise in the normal admin view, and
   * the point of the flag is to keep them out of it.
   */
  includeTestUsers: boolean;
  /** RevenueCat lifecycle filter (multi-select, union). Empty = all. */
  subscriptionFilter: SubscriptionKind[];
  /** Push-reachability filter (multi-select, union). Empty = all. */
  pushFilter: PushFilterKey[];
}

/**
 * Default status filter shows everything except `deleted` — tombstoned users are
 * hidden by default (noise + they only surface for the Remove Tombstone flow).
 * The Deleted chip re-adds them; the "All" chip clears the filter entirely.
 */
const DEFAULT_STATUS_FILTER: AccountStatus[] = ['active', 'disabled', 'pendingDeletion'];

const DEFAULT_VIEW_STATE: PersistedViewState = {
  version: VIEW_STATE_VERSION,
  sortField: 'user',
  sortDir: 'asc',
  selectedTypes: [],
  planFilter: [],
  statusFilter: DEFAULT_STATUS_FILTER,
  datePreset: 'any',
  submissionDisabledOnly: false,
  includeTestUsers: false,
  subscriptionFilter: [],
  pushFilter: [],
};

const VALID_SORT_FIELDS: SortField[] = ['user', 'plan', 'created', 'lastActive'];
/** Saved views from before the list was trimmed: the old activity sorts become Last active. */
const LEGACY_SORT_FIELDS: Record<string, SortField> = { lastLogin: 'lastActive', appOpened: 'lastActive' };
const VALID_TYPE_KEYS = USER_TYPE_FILTERS.map((f) => f.key);
const VALID_PLANS = PLAN_FILTERS.map((f) => f.key);
const VALID_STATUSES = STATUS_FILTERS.map((f) => f.key);
const VALID_PRESETS = DATE_PRESETS.map((f) => f.key);
const VALID_SUBSCRIPTION_KINDS = SUBSCRIPTION_FILTERS.map((f) => f.key);
const VALID_PUSH_KEYS = PUSH_FILTERS.map((f) => f.key);

/** Load persisted view state, defensively (a stale/corrupt blob must never crash the screen). */
function loadViewState(): PersistedViewState {
  try {
    const raw = localStorage.getItem(VIEW_STATE_KEY);
    if (!raw) return DEFAULT_VIEW_STATE;
    const parsed = JSON.parse(raw) as Partial<PersistedViewState>;
    // Pre-v2 blobs stored statusFilter `[]` (= show all, including deleted).
    // Migrate them to the new hide-deleted default; v2+ blobs keep their choice.
    const parsedVersion = typeof parsed.version === 'number' ? parsed.version : 1;
    const statusFilter =
      parsedVersion >= VIEW_STATE_VERSION && Array.isArray(parsed.statusFilter)
        ? parsed.statusFilter.filter((k): k is AccountStatus => VALID_STATUSES.includes(k as AccountStatus))
        : DEFAULT_STATUS_FILTER;
    return {
      version: VIEW_STATE_VERSION,
      sortField: VALID_SORT_FIELDS.includes(parsed.sortField as SortField)
        ? (parsed.sortField as SortField)
        : LEGACY_SORT_FIELDS[parsed.sortField as string] ?? DEFAULT_VIEW_STATE.sortField,
      sortDir: parsed.sortDir === 'desc' ? 'desc' : 'asc',
      selectedTypes: Array.isArray(parsed.selectedTypes)
        ? parsed.selectedTypes.filter((k): k is UserTypeKey => VALID_TYPE_KEYS.includes(k as UserTypeKey))
        : [],
      planFilter: Array.isArray(parsed.planFilter)
        ? parsed.planFilter.filter((k): k is UserPlan => VALID_PLANS.includes(k as UserPlan))
        : [],
      statusFilter,
      datePreset: VALID_PRESETS.includes(parsed.datePreset as DatePreset)
        ? (parsed.datePreset as DatePreset)
        : 'any',
      submissionDisabledOnly: parsed.submissionDisabledOnly === true,
      // Absent in blobs written before this filter existed, and `=== true`
      // reads that as false — which is the default. No VIEW_STATE_VERSION bump
      // needed, and bumping would reset everyone's saved status filter.
      includeTestUsers: parsed.includeTestUsers === true,
      subscriptionFilter: Array.isArray(parsed.subscriptionFilter)
        ? parsed.subscriptionFilter.filter((k): k is SubscriptionKind =>
            VALID_SUBSCRIPTION_KINDS.includes(k as SubscriptionKind),
          )
        : [],
      pushFilter: Array.isArray(parsed.pushFilter)
        ? parsed.pushFilter.filter((k): k is PushFilterKey =>
            VALID_PUSH_KEYS.includes(k as PushFilterKey),
          )
        : [],
    };
  } catch {
    return DEFAULT_VIEW_STATE;
  }
}

export function UserManagement() {
  const { user, isAdmin } = useAuth();
  const [users, setUsers] = useState<FirestoreUser[]>([]);
  const [showCreateBrandUser, setShowCreateBrandUser] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [editingUser, setEditingUser] = useState<FirestoreUser | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState<FirestoreUser | null>(null);
  const [confirmCancelDeletion, setConfirmCancelDeletion] = useState<FirestoreUser | null>(null);
  // Close-on-behalf (starts the 30-day deletion clock).
  const [confirmClose, setConfirmClose] = useState<FirestoreUser | null>(null);
  // Immediate hard-delete. Gated behind typing the target's email (AWS-style).
  const [confirmPurge, setConfirmPurge] = useState<FirestoreUser | null>(null);
  const [purgeTypedConfirm, setPurgeTypedConfirm] = useState('');
  const [purgeReason, setPurgeReason] = useState('');
  // Optional extra on the Purge flow: after the purge succeeds, also remove the
  // tombstone (deletes the accountStatus:'deleted' users doc) so the email is
  // freed for re-signup in one step. Reset when the modal closes.
  const [alsoRemoveTombstone, setAlsoRemoveTombstone] = useState(false);
  // Remove Tombstone (deletes the accountStatus:'deleted' users doc → frees the
  // email for re-signup). Gated behind typing the target's email, like Purge Now.
  const [confirmRemoveTombstone, setConfirmRemoveTombstone] = useState<FirestoreUser | null>(null);
  const [tombstoneTypedConfirm, setTombstoneTypedConfirm] = useState('');
  // Grant AI Credits (adds persistent bonusCredits, spent before the monthly
  // pool). Real token cost + permanent audit log, so gated behind typing the
  // target's email, like Purge Now. Current balance is fetched on open.
  const [grantUser, setGrantUser] = useState<FirestoreUser | null>(null);
  const [grantAmount, setGrantAmount] = useState('1');
  const [grantReason, setGrantReason] = useState('');
  const [grantTypedConfirm, setGrantTypedConfirm] = useState('');
  const [grantStatus, setGrantStatus] = useState<AiCreditStatus | null>(null);
  const [grantStatusLoading, setGrantStatusLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm, 250);

  // Sort + filter view state, restored from (and persisted to) localStorage so
  // it survives tab switches (UserManagement unmounts) and reloads. Search text
  // is intentionally not persisted.
  const initialView = useMemo(() => loadViewState(), []);
  const [sortField, setSortField] = useState<SortField>(initialView.sortField);
  const [sortDir, setSortDir] = useState<SortDir>(initialView.sortDir);
  // User-type filter (multi-select, union). Empty = show all types.
  const [selectedTypes, setSelectedTypes] = useState<UserTypeKey[]>(initialView.selectedTypes);
  // Plan/tier filter (multi-select, union). Empty = all plans.
  const [planFilter, setPlanFilter] = useState<UserPlan[]>(initialView.planFilter);
  // Account-status filter (multi-select, union). Empty = all statuses.
  const [statusFilter, setStatusFilter] = useState<AccountStatus[]>(initialView.statusFilter);
  // Created-date preset filter.
  const [datePreset, setDatePreset] = useState<DatePreset>(initialView.datePreset);
  // Flag filter: when true, show only users with submissions disabled.
  const [submissionDisabledOnly, setSubmissionDisabledOnly] = useState<boolean>(
    initialView.submissionDisabledOnly,
  );
  const [includeTestUsers, setIncludeTestUsers] = useState<boolean>(
    initialView.includeTestUsers,
  );
  // RevenueCat lifecycle filter (multi-select, union). Empty = all.
  const [subscriptionFilter, setSubscriptionFilter] = useState<SubscriptionKind[]>(
    initialView.subscriptionFilter,
  );
  // Push-reachability filter (multi-select, union). Empty = all.
  const [pushFilter, setPushFilter] = useState<PushFilterKey[]>(
    initialView.pushFilter,
  );

  // Watch counts (top-level `watches` where userId == uid) — loaded on demand,
  // never eagerly, since it is one aggregation query per user. uid -> count; a uid
  // absent from the map renders "—" (either not loaded or the count query was
  // denied by the watchlock rules).
  // The User Info screen, by uid so it follows the live snapshot.
  const [infoUid, setInfoUid] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  // Form state for editing
  const [editIsAdmin, setEditIsAdmin] = useState(false);
  const [editIsContributor, setEditIsContributor] = useState(false);
  const [editPlan, setEditPlan] = useState<UserPlan>('free');
  const [editSubmissionDisabled, setEditSubmissionDisabled] = useState(false);
  const [editIsTestUser, setEditIsTestUser] = useState(false);
  // AI limits. `null` means "no override stored" for both — the absence IS the
  // value, so neither can be modelled as a number/string with a sentinel.
  const [editCapOverride, setEditCapOverride] = useState<CapValue | null>(null);
  const [editRateLimitMode, setEditRateLimitMode] =
    useState<AiRateLimitMode | null>(null);

  // Live limiter config, for the effective-cap and global-mode readouts in the
  // edit modal. Fetched once when a modal first opens rather than on mount —
  // it is one RTDB read that most visits to this page never need.
  const [aiLimitConfig, setAiLimitConfig] = useState<AiRateLimitConfig | null>(
    null,
  );

  // Firebase Auth metadata overlay (fetched via Cloud Function, keyed by uid)
  const [authMeta, setAuthMeta] = useState<Record<string, AuthMetadata>>({});
  const [authMetaLoading, setAuthMetaLoading] = useState(false);

  // Push-device overlay (fetched via Cloud Function, keyed by uid). A uid
  // ABSENT from this map has no registered device — the callable resolves
  // every uid it is given — so absent is a real answer, not a pending one.
  // `pushLoaded` is what distinguishes "not fetched yet" from "zero devices".
  const [pushState, setPushState] = useState<Record<string, PushDeviceState>>({});
  const [pushLoaded, setPushLoaded] = useState(false);

  // Orphan users state
  const [orphanUsers, setOrphanUsers] = useState<OrphanUser[]>([]);
  const [orphanLoading, setOrphanLoading] = useState(false);
  const [orphanScanned, setOrphanScanned] = useState(false);
  const [confirmDeleteOrphan, setConfirmDeleteOrphan] = useState<OrphanUser | null>(null);

  useEffect(() => {
    setLoading(true);
    const unsubscribe = subscribeToUsers((fetchedUsers) => {
      // Sort by email
      const sorted = [...fetchedUsers].sort((a, b) =>
        (a.email || '').localeCompare(b.email || '')
      );
      setUsers(sorted);
      setLoading(false);
      setRefreshing(false);
    });

    return () => unsubscribe();
  }, [refreshKey]);

  // Fetch the target's current AI credit status when the Grant modal opens.
  // Super-admin gated server-side, so a non-super-admin gets a 403 here.
  useEffect(() => {
    if (!grantUser) {
      setGrantStatus(null);
      return;
    }
    let cancelled = false;
    setGrantStatusLoading(true);
    setGrantStatus(null);
    getUserAiCreditStatus(grantUser.uid)
      .then((status) => {
        if (!cancelled) setGrantStatus(status);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Error loading AI credit status:', error);
        const msg = error instanceof Error ? error.message : '';
        toast.error(
          /permission|super-?admin/i.test(msg)
            ? 'Requires super-admin access.'
            : 'Failed to load credit status.',
        );
      })
      .finally(() => {
        if (!cancelled) setGrantStatusLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [grantUser]);

  // Overlay Firebase Auth metadata for the current user set. Refetch whenever
  // the set of uids changes (the sorted-join key below is stable across
  // re-renders that don't add/remove users).
  const uidKey = users.map((u) => u.uid).sort().join(',');
  useEffect(() => {
    if (!uidKey) {
      setAuthMeta({});
      return;
    }
    let cancelled = false;
    setAuthMetaLoading(true);
    getUserAuthMetadata(uidKey.split(','))
      .then((meta) => {
        if (!cancelled) setAuthMeta(meta);
      })
      .catch((error) => {
        // Non-fatal: the table still renders with Firestore fields as fallback.
        console.error('Error fetching auth metadata:', error);
      })
      .finally(() => {
        if (!cancelled) setAuthMetaLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uidKey]);

  // Push-device state for the current user set. One collectionGroup scan
  // server-side, not one query per user — and it CANNOT be a client query at
  // all: the rule on users/{uid}/fcmTokens is owner-only with no admin branch.
  useEffect(() => {
    if (!uidKey) {
      setPushState({});
      setPushLoaded(false);
      return;
    }
    let cancelled = false;
    getUserPushDeviceState(uidKey.split(','))
      .then((state) => {
        if (cancelled) return;
        setPushState(state);
        setPushLoaded(true);
      })
      .catch((error) => {
        // Non-fatal: the column degrades to "—" rather than breaking the table.
        console.error('Error fetching push device state:', error);
      });
    return () => {
      cancelled = true;
    };
  }, [uidKey]);

  // Persist sort + filter selections (not search text) across unmounts/reloads.
  useEffect(() => {
    try {
      const state: PersistedViewState = {
        version: VIEW_STATE_VERSION,
        sortField,
        sortDir,
        selectedTypes,
        planFilter,
        statusFilter,
        datePreset,
        submissionDisabledOnly,
        includeTestUsers,
        subscriptionFilter,
        pushFilter,
      };
      localStorage.setItem(VIEW_STATE_KEY, JSON.stringify(state));
    } catch {
      // Best-effort; ignore quota/serialization failures.
    }
  }, [sortField, sortDir, selectedTypes, planFilter, statusFilter, datePreset, submissionDisabledOnly, includeTestUsers, subscriptionFilter, pushFilter]);

  const handleRefresh = () => {
    setRefreshing(true);
    setRefreshKey((k) => k + 1);
  };

  const currentUserId = user?.uid;

  // Apply the text search first. Chip counts (type/plan/status) are computed
  // over this search-filtered set (so counts narrow as you type) but ignore the
  // filter selections themselves (so toggling one chip doesn't change the
  // others' counts).
  const searchFiltered = users.filter((u) => {
    if (!debouncedSearch) return true;
    const term = debouncedSearch.toLowerCase();
    return (
      u.email?.toLowerCase().includes(term) ||
      u.displayName?.toLowerCase().includes(term) ||
      u.uid.toLowerCase().includes(term)
    );
  });

  const typeCounts = USER_TYPE_FILTERS.reduce(
    (acc, { key }) => {
      acc[key] = searchFiltered.filter((u) => matchesUserType(u, key)).length;
      return acc;
    },
    {} as Record<UserTypeKey, number>
  );

  const planCounts = PLAN_FILTERS.reduce(
    (acc, { key }) => {
      acc[key] = searchFiltered.filter((u) => u.plan === key).length;
      return acc;
    },
    {} as Record<UserPlan, number>
  );

  const statusCounts = STATUS_FILTERS.reduce(
    (acc, { key }) => {
      acc[key] = searchFiltered.filter((u) => getAccountStatus(u) === key).length;
      return acc;
    },
    {} as Record<AccountStatus, number>
  );

  const submissionOffCount = searchFiltered.filter((u) => u.submissionDisabled).length;
  const testUserCount = searchFiltered.filter((u) => u.isTestUser === true).length;

  // Derive every badge once per user list, keyed by uid: the filter counts,
  // the filter predicate and the table cell all need the same answer, and the
  // derivation reads a clock — recomputing it per consumer could disagree
  // across a day boundary. Costs no reads; the fields are already streamed.
  const subscriptionBadges = useMemo(() => {
    const now = new Date();
    const map = new Map<string, ReturnType<typeof deriveSubscriptionBadge>>();
    for (const u of users) map.set(u.uid, deriveSubscriptionBadge(u, now));
    return map;
  }, [users]);

  // Derived once per (user set, push state) pair and shared by the counts, the
  // filter predicate, the table cell and the edit modal — so they cannot
  // disagree. Only meaningful once `pushLoaded`; before that every user would
  // look like they had zero devices.
  const pushBadges = useMemo(() => {
    const map = new Map<string, ReturnType<typeof derivePushBadge>>();
    if (!pushLoaded) return map;
    for (const u of users) {
      map.set(
        u.uid,
        derivePushBadge(pushState[u.uid], {
          lastAppVersion: u.lastAppVersion,
          notificationPermission: u.notificationPermission,
        }),
      );
    }
    return map;
  }, [users, pushState, pushLoaded]);

  const pushCounts = PUSH_FILTERS.reduce(
    (acc, { key }) => {
      acc[key] = searchFiltered.filter((u) => {
        const badge = pushBadges.get(u.uid);
        return badge ? matchesPushFilter(badge, key) : false;
      }).length;
      return acc;
    },
    {} as Record<PushFilterKey, number>
  );

  const subscriptionCounts = SUBSCRIPTION_FILTERS.reduce(
    (acc, { key }) => {
      acc[key] = searchFiltered.filter(
        (u) => subscriptionBadges.get(u.uid)?.kind === key,
      ).length;
      return acc;
    },
    {} as Record<SubscriptionKind, number>
  );

  const toggleType = (key: UserTypeKey) => {
    setSelectedTypes((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const togglePlan = (key: UserPlan) => {
    setPlanFilter((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const togglePush = (key: PushFilterKey) => {
    setPushFilter((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const toggleSubscription = (key: SubscriptionKind) => {
    setSubscriptionFilter((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const toggleStatus = (key: AccountStatus) => {
    setStatusFilter((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      // Dates and counts read most-useful newest/highest-first.
      setSortDir(field === 'created' || field === 'lastActive' ? 'desc' : 'asc');
    }
  };

  const resetFilters = () => {
    setSelectedTypes([]);
    setPlanFilter([]);
    // Reset to the hide-deleted default, not "show all".
    setStatusFilter(DEFAULT_STATUS_FILTER);
    setDatePreset('any');
    setSubmissionDisabledOnly(false);
    // Back to hiding them, the same way the status filter resets to
    // hide-deleted rather than to "show everything".
    setIncludeTestUsers(false);
    setSubscriptionFilter([]);
    setPushFilter([]);
    setSearchTerm('');
  };

  // The status filter defaulting to "hide deleted" means it's non-empty at rest,
  // so it doesn't count as an "active filter" unless it diverges from the default.
  const statusFilterIsDefault =
    statusFilter.length === DEFAULT_STATUS_FILTER.length &&
    DEFAULT_STATUS_FILTER.every((s) => statusFilter.includes(s));

  const hasActiveFilters =
    !!searchTerm ||
    selectedTypes.length > 0 ||
    planFilter.length > 0 ||
    !statusFilterIsDefault ||
    submissionDisabledOnly ||
    // On is the deviation here, because the default hides them.
    includeTestUsers ||
    subscriptionFilter.length > 0 ||
    pushFilter.length > 0 ||
    datePreset !== 'any';

  // Union within each category, AND across categories. Empty category = all.
  const dateCutoff = datePresetCutoffMs(datePreset);
  const filteredOnly = searchFiltered.filter((u) => {
    if (selectedTypes.length > 0 && !selectedTypes.some((key) => matchesUserType(u, key))) {
      return false;
    }
    if (planFilter.length > 0 && !planFilter.includes(u.plan)) return false;
    if (statusFilter.length > 0 && !statusFilter.includes(getAccountStatus(u))) return false;
    if (submissionDisabledOnly && !u.submissionDisabled) return false;
    if (!includeTestUsers && u.isTestUser === true) return false;
    if (pushFilter.length > 0) {
      const badge = pushBadges.get(u.uid);
      // Until the fetch resolves nothing is filtered out — briefly showing an
      // unfiltered table beats blanking it and looking like "no matches".
      if (badge && !pushFilter.some((key) => matchesPushFilter(badge, key))) {
        return false;
      }
    }
    if (subscriptionFilter.length > 0) {
      const kind = subscriptionBadges.get(u.uid)?.kind;
      // A user with no RevenueCat history has no kind at all, so any active
      // subscription filter excludes them rather than bucketing them as free.
      if (!kind || !subscriptionFilter.includes(kind)) return false;
    }
    if (dateCutoff !== null) {
      const created = resolvedCreatedMs(u, authMeta[u.uid]);
      // Unknown creation date can't be confirmed in-range → excluded while a
      // date preset is active.
      if (created === null || created < dateCutoff) return false;
    }
    return true;
  });

  // Sort the filtered set. Nulls (unknown dates / unloaded watch counts) sort
  // last regardless of direction.
  const filteredUsers = useMemo(() => {
    const dirMul = sortDir === 'asc' ? 1 : -1;
    const cmpNullable = (a: number | null, b: number | null): number => {
      if (a === null && b === null) return 0;
      if (a === null) return 1; // a after b, ignoring direction
      if (b === null) return -1;
      return (a - b) * dirMul;
    };
    return [...filteredOnly].sort((a, b) => {
      switch (sortField) {
        case 'plan':
          return (PLAN_RANK[a.plan] - PLAN_RANK[b.plan]) * dirMul;
        case 'created':
          return cmpNullable(resolvedCreatedMs(a, authMeta[a.uid]), resolvedCreatedMs(b, authMeta[b.uid]));
        case 'lastActive':
          return cmpNullable(lastActive(a, authMeta[a.uid])?.ms ?? null, lastActive(b, authMeta[b.uid])?.ms ?? null);
        case 'user':
        default:
          return (a.email || '').localeCompare(b.email || '') * dirMul;
      }
    });
  }, [filteredOnly, sortField, sortDir, authMeta]);

  // Back to page 1 whenever the set or its order changes; paginate() clamps a
  // page left past the end by a shrinking snapshot.
  const filterKey = JSON.stringify([debouncedSearch, sortField, sortDir, selectedTypes, planFilter, statusFilter, datePreset, submissionDisabledOnly, includeTestUsers, subscriptionFilter, pushFilter]);
  useEffect(() => setPage(1), [filterKey]);
  const pageSlice = paginate(filteredUsers, page);
  const infoUser = infoUid ? users.find((u) => u.uid === infoUid) ?? null : null;

  const handleEdit = (userToEdit: FirestoreUser) => {
    setEditingUser(userToEdit);
    setEditIsAdmin(userToEdit.isAdmin);
    setEditIsContributor(userToEdit.isContributor || false);
    setEditPlan(userToEdit.plan);
    setEditSubmissionDisabled(userToEdit.submissionDisabled || false);
    setEditIsTestUser(userToEdit.isTestUser === true);
    setEditCapOverride(userToEdit.aiUsage?.capOverride ?? null);
    setEditRateLimitMode(userToEdit.aiRateLimitMode ?? null);
    if (!aiLimitConfig) {
      // Never rejects — falls back to the shipped config, so a failed read
      // degrades to showing the defaults rather than blocking the modal.
      void fetchAiRateLimitConfig().then(setAiLimitConfig);
    }
  };

  const handleSave = async () => {
    if (!editingUser) return;

    // Prevent current user from changing their own isAdmin
    if (editingUser.uid === currentUserId && editIsAdmin !== editingUser.isAdmin) {
      toast.error('You cannot change your own admin status');
      return;
    }

    // A cap watchlock cannot parse is not rejected there — `resolveCap` falls
    // back to the tier default. So an invalid value saved here would look like
    // it took and then quietly do nothing; block it at the form instead.
    if (editCapOverride !== null) {
      const check = checkCapValue(editCapOverride);
      if (!check.ok) {
        toast.error(`AI daily cap: ${check.reason.toLowerCase()}`);
        return;
      }
    }

    setIsProcessing(true);
    try {
      const updates: {
        isAdmin?: boolean;
        isContributor?: boolean;
        plan?: UserPlan;
        submissionDisabled?: boolean;
        isTestUser?: boolean;
      } = {};

      if (editIsAdmin !== editingUser.isAdmin) {
        updates.isAdmin = editIsAdmin;
      }
      if (editIsContributor !== (editingUser.isContributor || false)) {
        updates.isContributor = editIsContributor;
      }
      if (editPlan !== editingUser.plan) {
        updates.plan = editPlan;
      }
      if (editSubmissionDisabled !== (editingUser.submissionDisabled || false)) {
        updates.submissionDisabled = editSubmissionDisabled;
      }
      // An explicit `false` when it is turned off, never `undefined`.
      // `updateUserFields` is an allow-list over `updateDoc`: an undefined value
      // skips its branch, the key is never written, the merge keeps the stored
      // `true`, and the flag would look cleared until the row was reopened.
      if (editIsTestUser !== (editingUser.isTestUser === true)) {
        updates.isTestUser = editIsTestUser;
      }

      // Three independent writes to the same document, not one. `plan` and the
      // roles go through `updateUserFields`; the cap override has to be a
      // dotted-path write so it does not clobber the usage counter beside it;
      // the limiter mode is a separate top-level field. Firestore has no
      // client-side multi-field merge that spans those shapes, so they are
      // sequenced — and because they can fail independently, each one names
      // itself on failure rather than sharing a generic message.
      const uid = editingUser.uid;
      const priorCapOverride = editingUser.aiUsage?.capOverride ?? null;
      const priorRateLimitMode = editingUser.aiRateLimitMode ?? null;

      const steps: { label: string; run: () => Promise<void> }[] = [];

      if (Object.keys(updates).length > 0) {
        steps.push({
          label: 'plan and roles',
          run: () => updateUserFields(uid, updates),
        });
      }
      if (editCapOverride !== priorCapOverride) {
        steps.push({
          label: 'AI daily cap override',
          run: () =>
            setUserAiCapOverride(uid, editCapOverride ?? null),
        });
      }
      if (editRateLimitMode !== priorRateLimitMode) {
        steps.push({
          label: 'AI limiter override',
          run: () => setUserAiRateLimitMode(uid, editRateLimitMode),
        });
      }

      const done: string[] = [];
      for (const step of steps) {
        try {
          await step.run();
          done.push(step.label);
        } catch (error) {
          console.error(`Error updating ${step.label}:`, error);
          // Name the step that failed AND what already landed. A bare "failed"
          // after a partial sequence invites a retry that re-applies writes
          // which already succeeded, and hides which half of the form is stale.
          toast.error(
            done.length > 0
              ? `Saved ${done.join(' and ')}, but failed to save ${step.label}. The modal is still open — retry or revert that field.`
              : `Failed to save ${step.label}.`,
          );
          return;
        }
      }

      if (done.length > 0) {
        toast.success(`Updated ${done.join(', ')}`);
      }

      setEditingUser(null);
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleToggleDisabled = async (userToToggle: FirestoreUser) => {
    // Prevent current user from disabling themselves
    if (userToToggle.uid === currentUserId) {
      toast.error('You cannot disable your own account');
      return;
    }

    const status = getAccountStatus(userToToggle);
    const willDisable = status !== 'disabled';

    setIsProcessing(true);
    try {
      await setUserDisabled(userToToggle.uid, willDisable);
      toast.success(willDisable ? 'User account disabled' : 'User account enabled');
      setConfirmDisable(null);
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error('Failed to update user status. Make sure the Cloud Function is deployed.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelDeletion = async (userToCancel: FirestoreUser) => {
    setIsProcessing(true);
    try {
      await cancelPendingDeletion(userToCancel.uid);
      toast.success('Pending deletion cancelled');
      setConfirmCancelDeletion(null);
    } catch (error) {
      console.error('Error cancelling pending deletion:', error);
      toast.error('Failed to cancel pending deletion. Make sure the Cloud Function is deployed.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCloseOnBehalf = async (userToClose: FirestoreUser) => {
    if (userToClose.uid === currentUserId) {
      toast.error('You cannot close your own account');
      return;
    }
    setIsProcessing(true);
    try {
      await closeAccountOnBehalf(userToClose.uid);
      toast.success('Account deletion scheduled (30 days)');
      setConfirmClose(null);
    } catch (error) {
      console.error('Error closing account on behalf:', error);
      toast.error('Failed to close account. Make sure the Cloud Function is deployed.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePurgeNow = async (userToPurge: FirestoreUser) => {
    if (userToPurge.uid === currentUserId) {
      toast.error('You cannot purge your own account');
      return;
    }
    const target = userToPurge.email || userToPurge.uid;
    // Snapshot the checkbox before any await — state can change under us.
    const removeTombstone = alsoRemoveTombstone;
    setIsProcessing(true);
    try {
      const reason = purgeReason.trim();
      await purgeAccountNow(userToPurge.uid, reason || undefined);
      // Purge succeeded. If requested, remove the tombstone as a follow-up call
      // (two audit rows — one per action — which is a plus for traceability). A
      // failure here must not fail the whole flow: the purge already landed.
      if (removeTombstone) {
        try {
          await deleteUserTombstone(userToPurge.uid);
          toast.success(`Purged ${target} and removed tombstone`);
        } catch (tombstoneError) {
          console.error('Error removing tombstone after purge:', tombstoneError);
          toast.error('Purged, but failed to remove tombstone — remove manually.');
        }
      } else {
        toast.success(`Purged ${target}`);
      }
      setConfirmPurge(null);
    } catch (error) {
      console.error('Error purging account:', error);
      toast.error('Failed to purge account. Make sure the Cloud Function is deployed.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemoveTombstone = async (userToClear: FirestoreUser) => {
    setIsProcessing(true);
    try {
      await deleteUserTombstone(userToClear.uid);
      toast.success('Tombstone removed — email freed for re-signup');
      setConfirmRemoveTombstone(null);
    } catch (error) {
      console.error('Error removing tombstone:', error);
      // Mirrors the other account actions: the callable is super-admin gated
      // server-side, so a non-super-admin (or missing deployment) surfaces here.
      toast.error('Failed to remove tombstone. Requires super-admin access.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleGrantAiCredits = async (userToGrant: FirestoreUser) => {
    if (userToGrant.uid === currentUserId) {
      toast.error('You cannot grant credits to your own account');
      return;
    }
    const amount = Math.trunc(Number(grantAmount));
    const reason = grantReason.trim();
    // Mirror the server guards (MAX_GRANT_PER_CALL = 25, required reason).
    if (!Number.isFinite(amount) || amount < 1 || amount > 25 || !reason) {
      return;
    }
    setIsProcessing(true);
    try {
      const result = await grantAiCredits(userToGrant.uid, amount, reason);
      const target = userToGrant.email || userToGrant.uid;
      toast.success(
        `Granted ${amount} credit${amount === 1 ? '' : 's'} to ${target} — ` +
          `new bonus balance: ${result.newState.bonusCreditsRemaining ?? '—'}`,
      );
      setGrantUser(null);
    } catch (error) {
      console.error('Error granting AI credits:', error);
      const msg = error instanceof Error ? error.message : '';
      toast.error(
        /permission|super-?admin/i.test(msg)
          ? 'Requires super-admin access.'
          : msg || 'Failed to grant credits.',
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleScanOrphans = async () => {
    setOrphanLoading(true);
    try {
      const orphans = await getOrphanUsers();
      setOrphanUsers(orphans);
      setOrphanScanned(true);
      if (orphans.length === 0) {
        toast.success('No orphan users found');
      } else {
        toast.success(`Found ${orphans.length} orphan user(s)`);
      }
    } catch (error) {
      console.error('Error scanning for orphan users:', error);
      toast.error('Failed to scan for orphan users. Make sure the Cloud Function is deployed.');
    } finally {
      setOrphanLoading(false);
    }
  };

  const handleDeleteOrphan = async (orphan: OrphanUser) => {
    setIsProcessing(true);
    try {
      await deleteOrphanUser(orphan.uid);
      setOrphanUsers((prev) => prev.filter((u) => u.uid !== orphan.uid));
      toast.success('Orphan user deleted');
      setConfirmDeleteOrphan(null);
    } catch (error) {
      console.error('Error deleting orphan user:', error);
      toast.error('Failed to delete orphan user. Make sure the Cloud Function is deployed.');
    } finally {
      setIsProcessing(false);
    }
  };

  // The account actions the User Info screen offers. Each only opens the same
  // confirm modal the list used to, so the guards (typed confirms, self-checks)
  // are unchanged.
  const infoActionsFor = (u: FirestoreUser) => {
    const status = getAccountStatus(u);
    const isSelf = u.uid === currentUserId;
    return {
      onEdit: () => handleEdit(u),
      onRemoveTombstone:
        status === 'deleted'
          ? () => {
              setTombstoneTypedConfirm('');
              setConfirmRemoveTombstone(u);
            }
          : undefined,
      onCancelDeletion: status === 'pendingDeletion' ? () => setConfirmCancelDeletion(u) : undefined,
      onToggleSuspend: !isSelf && status !== 'pendingDeletion' ? () => setConfirmDisable(u) : undefined,
      onCloseOnBehalf: !isSelf && status !== 'pendingDeletion' ? () => setConfirmClose(u) : undefined,
      onPurge: !isSelf
        ? () => {
            setPurgeTypedConfirm('');
            setPurgeReason('');
            setAlsoRemoveTombstone(false);
            setConfirmPurge(u);
          }
        : undefined,
      onGrantCredits: !isSelf
        ? () => {
            setGrantAmount('1');
            setGrantReason('');
            setGrantTypedConfirm('');
            setGrantUser(u);
          }
        : undefined,
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {infoUser ? (
        <UserInfoScreen
          user={infoUser}
          status={getAccountStatus(infoUser)}
          isSelf={infoUser.uid === currentUserId}
          meta={authMeta[infoUser.uid]}
          subscriptionBadge={subscriptionBadges.get(infoUser.uid)}
          pushBadge={pushBadges.get(infoUser.uid)}
          pushDevices={pushState[infoUser.uid]}
          onBack={() => setInfoUid(null)}
          actions={infoActionsFor(infoUser)}
        />
      ) : (
      <>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-500 mt-1">
            {filteredUsers.length === users.length
              ? `${users.length} users`
              : `${filteredUsers.length} of ${users.length} users`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <Button
              variant="primary"
              onClick={() => setShowCreateBrandUser(true)}
            >
              + Create Brand User
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={handleRefresh}
            loading={refreshing}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <input
          type="text"
          placeholder="Search users by email, name, or ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-4 py-2 pl-10 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
        />
        <svg
          className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </div>

      {/* Filters. Counts reflect the current search only, so you can see what's
          there before clicking. Union within a row, AND across rows. */}
      <div className="space-y-2.5">
        {/* Type */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase w-16 shrink-0">Type</span>
          <FilterChip
            active={selectedTypes.length === 0}
            label="All"
            count={searchFiltered.length}
            onClick={() => setSelectedTypes([])}
          />
          {USER_TYPE_FILTERS.map(({ key, label }) => (
            <FilterChip
              key={key}
              active={selectedTypes.includes(key)}
              label={label}
              count={typeCounts[key]}
              onClick={() => toggleType(key)}
            />
          ))}
        </div>

        {/* Plan / tier */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase w-16 shrink-0">Plan</span>
          <FilterChip
            active={planFilter.length === 0}
            label="All"
            onClick={() => setPlanFilter([])}
          />
          {PLAN_FILTERS.map(({ key, label }) => (
            <FilterChip
              key={key}
              active={planFilter.includes(key)}
              label={label}
              count={planCounts[key]}
              onClick={() => togglePlan(key)}
            />
          ))}
        </div>

        {/* Push reachability */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase w-16 shrink-0">Push</span>
          <FilterChip
            active={pushFilter.length === 0}
            label="All"
            onClick={() => setPushFilter([])}
          />
          {PUSH_FILTERS.map(({ key, label }) => (
            <FilterChip
              key={key}
              active={pushFilter.includes(key)}
              label={label}
              count={pushCounts[key]}
              onClick={() => togglePush(key)}
            />
          ))}
        </div>

        {/* RevenueCat lifecycle */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase w-16 shrink-0">Sub</span>
          <FilterChip
            active={subscriptionFilter.length === 0}
            label="All"
            onClick={() => setSubscriptionFilter([])}
          />
          {SUBSCRIPTION_FILTERS.map(({ key, label }) => (
            <FilterChip
              key={key}
              active={subscriptionFilter.includes(key)}
              label={label}
              count={subscriptionCounts[key]}
              onClick={() => toggleSubscription(key)}
            />
          ))}
        </div>

        {/* Account status */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase w-16 shrink-0">Status</span>
          <FilterChip
            active={statusFilter.length === 0}
            label="All"
            onClick={() => setStatusFilter([])}
          />
          {STATUS_FILTERS.map(({ key, label }) => (
            <FilterChip
              key={key}
              active={statusFilter.includes(key)}
              label={label}
              count={statusCounts[key]}
              onClick={() => toggleStatus(key)}
            />
          ))}
        </div>

        {/* Flags */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase w-16 shrink-0">Flags</span>
          <FilterChip
            active={submissionDisabledOnly}
            label="Submissions off"
            count={submissionOffCount}
            onClick={() => setSubmissionDisabledOnly((v) => !v)}
          />
          {/*
            Reads the opposite way round to its neighbour — on ADDS rows rather
            than narrowing to them — so the label has to carry that, not the
            chip's active state. "Include" is doing the work.
          */}
          <FilterChip
            active={includeTestUsers}
            label="Include test users"
            count={testUserCount}
            onClick={() => setIncludeTestUsers((v) => !v)}
          />
        </div>

        {/* Created-date preset + watch-count loader + reset */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase w-16 shrink-0">Created</span>
          <select
            value={datePreset}
            onChange={(e) => setDatePreset(e.target.value as DatePreset)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          >
            {DATE_PRESETS.map(({ key, label }) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>

          <div className="flex items-center gap-2 ml-auto">
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                Reset filters
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Users Table — six columns for scanning; everything else is on the
          User Info screen (click the email). */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader label="User" field="user" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader
                  label="Subscription"
                  field="plan"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                  title="Plan tier, plus the RevenueCat lifecycle state when there is one. Sorts by tier."
                />
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Flags</th>
                <SortableHeader label="Signed up" field="created" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader
                  label="Last active"
                  field="lastActive"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                  title="Last app open; falls back to the Auth token refresh, then the last sign-in, for builds that do not report app opens."
                />
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {pageSlice.items.map((u) => {
                const status = getAccountStatus(u);
                const meta = authMeta[u.uid];
                const isSelf = u.uid === currentUserId;
                const badge = subscriptionBadges.get(u.uid);
                const active = lastActive(u, meta);
                const rowBg =
                  status === 'deleted'
                    ? 'bg-gray-50'
                    : status === 'disabled'
                    ? 'bg-red-50'
                    : status === 'pendingDeletion'
                    ? 'bg-amber-50'
                    : isSelf
                    ? 'bg-blue-50'
                    : '';
                return (
                  <tr key={u.uid} className={rowBg}>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => setInfoUid(u.uid)}
                        className="flex flex-col text-left group"
                        title="Open User Info"
                      >
                        <span className="text-sm font-medium text-gray-900 group-hover:text-brand-600 group-hover:underline">
                          {u.email || 'No email'}
                        </span>
                        {u.displayName && <span className="text-xs text-gray-500">{u.displayName}</span>}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1">
                        <Badge variant={
                          u.plan === 'horologist' ? 'success' :
                          u.plan === 'curator' ? 'warning' :
                          u.plan === 'collector' ? 'info' : 'default'
                        }>
                          {u.plan === 'horologist' ? 'Horologist' :
                           u.plan === 'curator' ? 'Curator' :
                           u.plan === 'collector' ? 'Collector' : 'Free'}
                        </Badge>
                        {/* Lifecycle state only when RevenueCat has touched the
                            account, so "never subscribed" stays distinct from
                            "subscription ended". */}
                        {badge && (
                          <span title={badge.title}>
                            <Badge variant={badge.variant}>{badge.label}</Badge>
                          </span>
                        )}
                        {badge?.sandbox && (
                          <span className="text-xs text-amber-600" title="RevenueCat SANDBOX purchase — not real revenue" aria-label="Sandbox purchase">
                            ⌂
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {status !== 'active' && <StatusBadge status={status} />}
                        {u.isAdmin && <Badge variant="warning">Admin</Badge>}
                        {u.isContributor && <Badge variant="info">Contributor</Badge>}
                        {u.submissionDisabled && <Badge variant="danger">Submissions off</Badge>}
                        {u.isTestUser === true && <Badge variant="default">TEST</Badge>}
                        {isSelf && <span className="text-xs text-blue-600">(You)</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                      {meta?.creationTime ? (
                        <span title={new Date(meta.creationTime).toLocaleString()}>
                          {formatRelativeDate(meta.creationTime)}
                        </span>
                      ) : authMetaLoading ? (
                        <span className="inline-block h-4 w-16 rounded bg-gray-100 animate-pulse" />
                      ) : (
                        formatDate(u.createdAt)
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      {active ? (
                        <span
                          className={active.source === 'app' ? 'text-gray-700' : 'text-gray-400'}
                          title={`${
                            active.source === 'app'
                              ? 'App opened'
                              : active.source === 'refresh'
                              ? 'No app-open recorded; Auth token refresh'
                              : 'No app-open recorded; last sign-in'
                          } ${new Date(active.ms).toLocaleString()}`}
                        >
                          {active.source === 'app' ? '' : '~'}
                          {formatCompactAge(new Date(active.ms))}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setInfoUid(u.uid)}>
                          Info
                        </Button>
                        {status !== 'deleted' && (
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(u)}>
                            Edit
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {hasActiveFilters ? 'No users match your filters' : 'No users found'}
          </div>
        ) : (
          <UsersPager slice={pageSlice} onPage={setPage} />
        )}
      </div>

      {/* Orphan Users Section */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Orphan Users</h2>
            <p className="text-sm text-gray-500">
              Users in Firestore without a matching Firebase Authentication record
            </p>
          </div>
          <Button
            onClick={handleScanOrphans}
            loading={orphanLoading}
            variant="secondary"
          >
            {orphanScanned ? 'Rescan' : 'Scan for Orphans'}
          </Button>
        </div>

        {orphanScanned && (
          <div className="overflow-x-auto">
            {orphanUsers.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      User
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Created
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {orphanUsers.map((orphan) => (
                    <tr key={orphan.uid} className="bg-yellow-50">
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">
                            {orphan.email || 'No email'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {orphan.displayName || 'No display name'}
                          </span>
                          <span className="text-xs text-gray-400 font-mono">
                            {orphan.uid}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {formatDate(orphan.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setConfirmDeleteOrphan(orphan)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No orphan users found. All Firestore users have matching Auth records.
              </div>
            )}
          </div>
        )}

        {!orphanScanned && (
          <div className="text-center py-8 text-gray-500">
            Click "Scan for Orphans" to check for users without Auth records.
          </div>
        )}
      </div>

      </>
      )}

      {/* Create Brand User Modal (super admin only) */}
      {isAdmin && (
        <CreateBrandUserModal
          isOpen={showCreateBrandUser}
          onClose={() => setShowCreateBrandUser(false)}
          onCreated={handleRefresh}
        />
      )}

      {/* Edit Modal */}
      <Modal
        isOpen={!!editingUser}
        onClose={() => setEditingUser(null)}
        title="Edit User"
        size="md"
      >
        {editingUser && (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm font-medium text-gray-900">
                {editingUser.email || 'No email'}
              </p>
              <p className="text-xs text-gray-500 font-mono mt-1">
                {editingUser.uid}
              </p>
            </div>

            {/* Plan Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Plan
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="plan"
                    value="free"
                    checked={editPlan === 'free'}
                    onChange={() => setEditPlan('free')}
                    className="text-brand-600 focus:ring-brand-500"
                  />
                  <span className="text-sm text-gray-700">Free</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="plan"
                    value="collector"
                    checked={editPlan === 'collector'}
                    onChange={() => setEditPlan('collector')}
                    className="text-brand-600 focus:ring-brand-500"
                  />
                  <span className="text-sm text-gray-700">Collector</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="plan"
                    value="curator"
                    checked={editPlan === 'curator'}
                    onChange={() => setEditPlan('curator')}
                    className="text-brand-600 focus:ring-brand-500"
                  />
                  <span className="text-sm text-gray-700">Curator</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="plan"
                    value="horologist"
                    checked={editPlan === 'horologist'}
                    onChange={() => setEditPlan('horologist')}
                    className="text-brand-600 focus:ring-brand-500"
                  />
                  <span className="text-sm text-gray-700">Horologist</span>
                </label>
              </div>
            </div>

            {/* AI limits. Both fields are server-owned and written with admin
                credentials; see setUserAiCapOverride for why the cap is a
                dotted-path write. The plan radios above feed the effective-cap
                readout, so an unsaved tier change previews its own new cap. */}
            <div className="space-y-4 border-t border-gray-200 pt-4">
              <AiCapOverrideField
                plan={editPlan}
                caps={aiLimitConfig?.caps ?? null}
                mode={aiLimitConfig?.mode ?? null}
                usageCount={editingUser.aiUsage?.count}
                usageDay={editingUser.aiUsage?.day}
                value={editCapOverride}
                onChange={setEditCapOverride}
              />
              <AiRateLimitModeField
                globalMode={aiLimitConfig?.mode ?? null}
                value={editRateLimitMode}
                onChange={setEditRateLimitMode}
              />
            </div>

            {/* Role Toggles */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Roles
              </label>
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editIsAdmin}
                    onChange={(e) => setEditIsAdmin(e.target.checked)}
                    disabled={editingUser.uid === currentUserId}
                    className="rounded text-brand-600 focus:ring-brand-500"
                  />
                  <span className="text-sm text-gray-700">Administrator</span>
                </label>
                {editingUser.uid === currentUserId && (
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    You cannot change your own admin status
                  </p>
                )}
              </div>
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editIsContributor}
                    onChange={(e) => setEditIsContributor(e.target.checked)}
                    className="rounded text-brand-600 focus:ring-brand-500"
                  />
                  <span className="text-sm text-gray-700">Contributor</span>
                </label>
                <p className="text-xs text-gray-500 mt-1 ml-6">
                  Can create and edit their own articles
                </p>
              </div>
            </div>

            {/* Submission Controls */}
            <div className="space-y-3 border-t pt-4">
              <label className="block text-sm font-medium text-gray-700">
                Watch Database Submissions
              </label>
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editSubmissionDisabled}
                    onChange={(e) => setEditSubmissionDisabled(e.target.checked)}
                    className="rounded text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-700">Submissions disabled</span>
                </label>
                <p className="text-xs text-gray-500 mt-1 ml-6">
                  When checked, this user cannot submit references or images to
                  the Watch Database, regardless of tier or tenure.
                </p>
              </div>
            </div>

            {/* Account classification */}
            <div className="space-y-3 border-t pt-4">
              <label className="block text-sm font-medium text-gray-700">Account Type</label>
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editIsTestUser}
                    onChange={(e) => setEditIsTestUser(e.target.checked)}
                    className="rounded text-brand-600 focus:ring-brand-500"
                  />
                  <span className="text-sm text-gray-700">Test / internal user</span>
                </label>
                <p className="text-xs text-gray-500 mt-1 ml-6">
                  Hidden from this list by default, and excluded from aggregate analytics.
                  Notification suppression and the signup auto-flag ship separately.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="secondary"
                onClick={() => setEditingUser(null)}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button onClick={handleSave} loading={isProcessing}>
                Save
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Suspend / Restore Confirmation Modal */}
      <Modal
        isOpen={!!confirmDisable}
        onClose={() => setConfirmDisable(null)}
        title={
          confirmDisable && getAccountStatus(confirmDisable) === 'disabled'
            ? 'Restore User'
            : 'Suspend User'
        }
        size="sm"
      >
        {confirmDisable && (() => {
          const status = getAccountStatus(confirmDisable);
          const isCurrentlyDisabled = status === 'disabled';
          return (
            <div className="space-y-4">
              <p className="text-gray-600">
                {isCurrentlyDisabled
                  ? 'Are you sure you want to restore this user? They will be able to sign in again.'
                  : 'Are you sure you want to suspend this user? They will not be able to sign in until restored. Suspension is indefinite and reversible.'}
              </p>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm font-medium text-gray-900">
                  {confirmDisable.email || 'No email'}
                </p>
              </div>
              <div className="flex justify-end gap-3">
                <Button
                  variant="secondary"
                  onClick={() => setConfirmDisable(null)}
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
                <Button
                  variant={isCurrentlyDisabled ? 'primary' : 'danger'}
                  onClick={() => handleToggleDisabled(confirmDisable)}
                  loading={isProcessing}
                >
                  {isCurrentlyDisabled ? 'Restore User' : 'Suspend User'}
                </Button>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Cancel Deletion Confirmation Modal */}
      <Modal
        isOpen={!!confirmCancelDeletion}
        onClose={() => setConfirmCancelDeletion(null)}
        title="Cancel Pending Deletion"
        size="sm"
      >
        {confirmCancelDeletion && (
          <div className="space-y-4">
            <p className="text-gray-600">
              Are you sure you want to cancel this user's pending deletion?
              Their account will be restored to active status and the scheduled
              purge will be removed.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
              <p className="text-sm font-medium text-gray-900">
                {confirmCancelDeletion.email || 'No email'}
              </p>
              <p className="text-xs text-gray-600">
                Requested: {formatDate(confirmCancelDeletion.deletionRequestedAt)}
              </p>
              <p className="text-xs text-gray-600">
                Scheduled: {formatDate(confirmCancelDeletion.deletionScheduledAt)}
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => setConfirmCancelDeletion(null)}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => handleCancelDeletion(confirmCancelDeletion)}
                loading={isProcessing}
              >
                Cancel Deletion
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Close-on-behalf Confirmation Modal (starts 30-day deletion clock) */}
      <Modal
        isOpen={!!confirmClose}
        onClose={() => setConfirmClose(null)}
        title="Close Account on Behalf"
        size="sm"
      >
        {confirmClose && (
          <div className="space-y-4">
            <p className="text-gray-600">
              This will start a 30-day countdown, after which the account and all
              data will be permanently deleted. The user can be restored via
              "Cancel Deletion" before then.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm font-medium text-gray-900">
                {confirmClose.email || 'No email'}
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => setConfirmClose(null)}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => handleCloseOnBehalf(confirmClose)}
                loading={isProcessing}
              >
                Close Account
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Purge Now Confirmation Modal (immediate, irreversible hard-delete) */}
      <Modal
        isOpen={!!confirmPurge}
        onClose={() => {
          setConfirmPurge(null);
          setAlsoRemoveTombstone(false);
        }}
        title="Purge Account Now"
        size="md"
      >
        {confirmPurge && (() => {
          // Gate the confirm on typing the target's email (AWS-style). If the
          // user has no email, fall back to requiring the uid so an empty input
          // can never satisfy the gate.
          const expected = (confirmPurge.email || confirmPurge.uid).trim();
          const matches =
            purgeTypedConfirm.trim().toLowerCase() === expected.toLowerCase();
          return (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm font-medium text-red-800">
                  This action is IRREVERSIBLE. All watches, subcollections,
                  storage, and the user doc will be permanently deleted.
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm font-medium text-gray-900">
                  {confirmPurge.email || 'No email'}
                </p>
                <p className="text-xs text-gray-500 font-mono mt-1">
                  {confirmPurge.uid}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason (optional)
                </label>
                <textarea
                  value={purgeReason}
                  onChange={(e) => setPurgeReason(e.target.value)}
                  rows={2}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
                  placeholder="Logged to the audit trail"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type <span className="font-mono">{expected}</span> to confirm
                </label>
                <input
                  type="text"
                  value={purgeTypedConfirm}
                  onChange={(e) => setPurgeTypedConfirm(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
                  placeholder={expected}
                  autoComplete="off"
                />
              </div>
              {/* Optional extra: fold the Remove Tombstone step into this action.
                  Muted grey — a secondary option, not the primary purge action. */}
              <label className="flex items-start gap-2 text-sm text-gray-500 cursor-pointer">
                <input
                  type="checkbox"
                  checked={alsoRemoveTombstone}
                  onChange={(e) => setAlsoRemoveTombstone(e.target.checked)}
                  disabled={isProcessing}
                  className="mt-0.5 rounded border-gray-300 text-gray-600 focus:ring-gray-400"
                />
                <span>
                  Also remove tombstone (allows this email to sign up again)
                  <span className="block text-xs text-gray-400 mt-0.5">
                    Frees this email to sign up again. Watches and wiped fields
                    still cannot be restored.
                  </span>
                </span>
              </label>
              <div className="flex justify-end gap-3">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setConfirmPurge(null);
                    setAlsoRemoveTombstone(false);
                  }}
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={() => handlePurgeNow(confirmPurge)}
                  loading={isProcessing}
                  disabled={!matches || isProcessing}
                >
                  Purge Account
                </Button>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Remove Tombstone Confirmation Modal (frees a purged email for re-signup) */}
      <Modal
        isOpen={!!confirmRemoveTombstone}
        onClose={() => setConfirmRemoveTombstone(null)}
        title="Remove Tombstone"
        size="md"
      >
        {confirmRemoveTombstone && (() => {
          // Gate the confirm on typing the target's email (like Purge Now). If
          // the user has no email, fall back to the uid so an empty input can
          // never satisfy the gate.
          const expected = (confirmRemoveTombstone.email || confirmRemoveTombstone.uid).trim();
          const matches =
            tombstoneTypedConfirm.trim().toLowerCase() === expected.toLowerCase();
          return (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm font-medium text-amber-800">
                  This removes the deleted tombstone for this email, freeing it to
                  sign up again. The user's watches and wiped account fields are
                  NOT restored — a re-signup creates a fresh account.
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm font-medium text-gray-900">
                  {confirmRemoveTombstone.email || 'No email'}
                </p>
                <p className="text-xs text-gray-500 font-mono mt-1">
                  {confirmRemoveTombstone.uid}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type <span className="font-mono">{expected}</span> to confirm
                </label>
                <input
                  type="text"
                  value={tombstoneTypedConfirm}
                  onChange={(e) => setTombstoneTypedConfirm(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  placeholder={expected}
                  autoComplete="off"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button
                  variant="secondary"
                  onClick={() => setConfirmRemoveTombstone(null)}
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={() => handleRemoveTombstone(confirmRemoveTombstone)}
                  loading={isProcessing}
                  disabled={!matches || isProcessing}
                >
                  Remove Tombstone
                </Button>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Grant AI Credits Modal (adds persistent bonusCredits to a user) */}
      <Modal
        isOpen={!!grantUser}
        onClose={() => setGrantUser(null)}
        title="Grant AI Credits"
        size="md"
      >
        {grantUser && (() => {
          // Gate the confirm on typing the target's email (like Purge Now) —
          // credits carry a real token cost and the reason lives forever in the
          // audit log. Fall back to the uid so an empty input never satisfies it.
          const expected = (grantUser.email || grantUser.uid).trim();
          const typedMatches =
            grantTypedConfirm.trim().toLowerCase() === expected.toLowerCase();
          const amount = Math.trunc(Number(grantAmount));
          const amountValid =
            Number.isFinite(amount) && amount >= 1 && amount <= 25;
          const reasonValid = grantReason.trim().length > 0;
          const canSubmit =
            amountValid && reasonValid && typedMatches && !isProcessing;
          return (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm font-medium text-gray-900">
                  {grantUser.email || 'No email'}
                </p>
                <p className="text-xs text-gray-500 font-mono mt-1">
                  {grantUser.uid}
                </p>
              </div>

              {/* Current credit status (fetched on open) */}
              <div className="rounded-lg border border-gray-200 p-3">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">
                  Current credit status
                </p>
                {grantStatusLoading ? (
                  <p className="text-sm text-gray-500">Loading…</p>
                ) : grantStatus ? (
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <dt className="text-gray-500">Tier</dt>
                    <dd className="text-gray-900 text-right">
                      {grantStatus.tier ?? '—'}
                    </dd>
                    <dt className="text-gray-500">Remaining</dt>
                    <dd className="text-gray-900 text-right">
                      {grantStatus.remaining ?? '—'}
                    </dd>
                    <dt className="text-gray-500">Monthly limit</dt>
                    <dd className="text-gray-900 text-right">
                      {grantStatus.monthlyLimit ?? '—'}
                    </dd>
                    <dt className="text-gray-500">Bonus remaining</dt>
                    <dd className="text-gray-900 text-right font-medium">
                      {grantStatus.bonusCreditsRemaining ?? '—'}
                    </dd>
                    <dt className="text-gray-500">Intro remaining</dt>
                    <dd className="text-gray-900 text-right">
                      {grantStatus.introCreditsRemaining ?? '—'}
                    </dd>
                    <dt className="text-gray-500">Resets</dt>
                    <dd className="text-gray-900 text-right">
                      {grantStatus.resetsAt
                        ? grantStatus.resetsAt.toLocaleDateString()
                        : '—'}
                    </dd>
                  </dl>
                ) : (
                  <p className="text-sm text-gray-500">
                    Status unavailable.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount (1–25)
                </label>
                <input
                  type="number"
                  min={1}
                  max={25}
                  step={1}
                  value={grantAmount}
                  onChange={(e) => setGrantAmount(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  placeholder="1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Max 25 credits per grant.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={grantReason}
                  onChange={(e) => setGrantReason(e.target.value)}
                  rows={2}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  placeholder="Refund for failed identify — support ticket #123 / Comp for outage on 2026-07-24"
                />
                <p className="text-xs text-amber-600 mt-1">
                  This text is permanent — it lives forever in the admin audit log.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type <span className="font-mono">{expected}</span> to confirm
                </label>
                <input
                  type="text"
                  value={grantTypedConfirm}
                  onChange={(e) => setGrantTypedConfirm(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  placeholder={expected}
                  autoComplete="off"
                />
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  variant="secondary"
                  onClick={() => setGrantUser(null)}
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => handleGrantAiCredits(grantUser)}
                  loading={isProcessing}
                  disabled={!canSubmit}
                >
                  Grant Credits
                </Button>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Delete Orphan Confirmation Modal */}
      <Modal
        isOpen={!!confirmDeleteOrphan}
        onClose={() => setConfirmDeleteOrphan(null)}
        title="Delete Orphan User"
        size="sm"
      >
        {confirmDeleteOrphan && (
          <div className="space-y-4">
            <p className="text-gray-600">
              Are you sure you want to delete this orphan user from Firestore?
              This user has no Firebase Authentication record.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm font-medium text-gray-900">
                {confirmDeleteOrphan.email || 'No email'}
              </p>
              <p className="text-xs text-gray-500 font-mono mt-1">
                {confirmDeleteOrphan.uid}
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => setConfirmDeleteOrphan(null)}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => handleDeleteOrphan(confirmDeleteOrphan)}
                loading={isProcessing}
              >
                Delete
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
